// src/modules/dashboard/components/CompetitionsOverview/index.js
import React from 'react';
import { Card, Badge, ProgressBar } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { format, formatDistanceToNow } from 'date-fns';
import { parseLocalDate } from '../../../../shared/utils/dateUtils';
import './styles.css';

const CompetitionCard = ({ competition, variant }) => {
    const isActive = variant === 'active';
    const isUpcoming = variant === 'upcoming';
    const isConcluded = variant === 'concluded';
    const isUserParticipating = competition.isUserJoined;

    const progress = isUserParticipating && competition.pointTarget
        ? Math.min((competition.userPoints / competition.pointTarget) * 100, 100)
        : 0;
    const isAboveThreshold = competition.userPoints >= competition.pointTarget;

    const getStatusBadge = () => {
        if (isActive) return <Badge bg="success">Active</Badge>;
        if (isUpcoming) return <Badge bg="info">Upcoming</Badge>;
        if (isConcluded) return <Badge bg="secondary">Concluded</Badge>;
        return null;
    };

    const getDateInfo = () => {
        if (isActive) {
            return (
                <>
                    Ends {formatDistanceToNow(parseLocalDate(competition.endDate), { addSuffix: true })}
                </>
            );
        }
        if (isUpcoming) {
            return (
                <>
                    Starts {format(parseLocalDate(competition.startDate), 'MMM d, HH:mm')}
                </>
            );
        }
        if (isConcluded) {
            return (
                <>
                    Ended {format(parseLocalDate(competition.endDate), 'MMM d, yyyy HH:mm')}
                </>
            );
        }
        return '';
    };

    return (
        <Link
            to={`/competition/${competition.competitionId}`}
            className={`competition-card competition-card-${variant}`}
        >
            <div className="competition-card-header">
                <div className="competition-card-title">
                    <h6 className="competition-name">{competition.name}</h6>
                    {getStatusBadge()}
                </div>
                <span className="competition-date">{getDateInfo()}</span>
            </div>

            {isActive && isUserParticipating && (
                <div className="competition-card-progress">
                    <ProgressBar
                        now={progress}
                        variant={isAboveThreshold ? "success" : "primary"}
                        className="competition-progress-bar"
                    />
                    <div className="competition-card-stats">
                        <div className="comp-stat">
                            <span className="comp-stat-value">#{competition.currentPosition || '-'}</span>
                            <span className="comp-stat-label">Position</span>
                        </div>
                        <div className="comp-stat">
                            <span className="comp-stat-value">{competition.userPoints?.toFixed(1) || 0}</span>
                            <span className="comp-stat-label">Points</span>
                        </div>
                        <div className="comp-stat">
                            <span className="comp-stat-value">{competition.pointTarget?.toFixed(1) || 0}</span>
                            <span className="comp-stat-label">Target</span>
                        </div>
                        <div className="comp-stat">
                            <span className="comp-stat-value">{competition.participantCount || competition.competitors?.length || 0}</span>
                            <span className="comp-stat-label">Competitors</span>
                        </div>
                    </div>
                </div>
            )}

            {isActive && !isUserParticipating && (
                <div className="competition-card-progress">
                    <div className="competition-card-stats">
                        <div className="comp-stat">
                            <span className="comp-stat-value">{competition.pointTarget?.toFixed(1) || 0}</span>
                            <span className="comp-stat-label">Target</span>
                        </div>
                        <div className="comp-stat">
                            <span className="comp-stat-value">{competition.participantCount || competition.competitors?.length || 0}</span>
                            <span className="comp-stat-label">Competitors</span>
                        </div>
                    </div>
                    <div className="competition-card-join-cta">
                        <span className="join-cta">Join this competition</span>
                    </div>
                </div>
            )}

            {isConcluded && (
                <div className="competition-card-result">
                    {competition.winner && (
                        <div className="competition-card-winner">
                            <i className="bi bi-trophy-fill text-warning me-2"></i>
                            <span>Winner: {competition.winnerName || competition.winner}</span>
                        </div>
                    )}
                    <span className="concluded-participants">
                        <i className="bi bi-people me-1"></i>
                        {competition.participantCount || competition.competitors?.length || 0} competitors
                    </span>
                </div>
            )}

            {isUpcoming && (
                <div className="competition-card-join">
                    <span className="join-cta">
                        {isUserParticipating ? "You're signed up" : "Sign up early"}
                    </span>
                    <span className="join-info">
                        <span className="join-target">Target: {competition.pointTarget?.toFixed(1)} pts</span>
                        <span className="join-participants">
                            <i className="bi bi-people me-1"></i>
                            {competition.participantCount || competition.competitors?.length || 0} joined
                        </span>
                    </span>
                </div>
            )}
        </Link>
    );
};

const CompetitionSection = ({ title, competitions, variant, emptyMessage }) => {
    if (!competitions || competitions.length === 0) {
        return null;
    }

    return (
        <div className={`competition-section competition-section-${variant}`}>
            <h5 className="competition-section-title">
                {title}
                <Badge bg={variant === 'active' ? 'success' : variant === 'upcoming' ? 'info' : 'secondary'} className="ms-2">
                    {competitions.length}
                </Badge>
            </h5>
            <div className="competition-section-list">
                {competitions.map(competition => (
                    <CompetitionCard
                        key={competition.competitionId}
                        competition={competition}
                        variant={variant}
                    />
                ))}
            </div>
        </div>
    );
};

const CompetitionsOverview = ({
    activeCompetitions = [],
    upcomingCompetitions = [],
    concludedCompetitions = []
}) => {
    const participating = activeCompetitions.filter((c) => c.isUserJoined);
    const otherActive = activeCompetitions.filter((c) => !c.isUserJoined);

    const hasAnyCompetitions =
        activeCompetitions.length > 0 ||
        upcomingCompetitions.length > 0 ||
        concludedCompetitions.length > 0;

    return (
        <Card className="competitions-overview-card">
            <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                    <i className="bi bi-trophy me-2"></i>
                    Competitions
                </h5>
                <Link to="/competitions" className="btn btn-sm btn-outline-primary">
                    View All
                </Link>
            </Card.Header>
            <Card.Body>
                {hasAnyCompetitions ? (
                    <div className="competitions-overview-content">
                        <CompetitionSection
                            title="Participating"
                            competitions={participating}
                            variant="active"
                        />
                        <CompetitionSection
                            title="Active"
                            competitions={otherActive}
                            variant="active"
                        />
                        <CompetitionSection
                            title="Upcoming"
                            competitions={upcomingCompetitions}
                            variant="upcoming"
                        />
                        <CompetitionSection
                            title="Recently Concluded"
                            competitions={concludedCompetitions.slice(0, 3)}
                            variant="concluded"
                        />
                    </div>
                ) : (
                    <div className="competitions-empty">
                        <i className="bi bi-trophy"></i>
                        <p>No competitions available</p>
                        <Link to="/competitions" className="btn btn-primary btn-sm">
                            Browse Competitions
                        </Link>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

CompetitionCard.propTypes = {
    competition: PropTypes.shape({
        competitionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
        startDate: PropTypes.string,
        endDate: PropTypes.string,
        pointTarget: PropTypes.number,
        userPoints: PropTypes.number,
        currentPosition: PropTypes.number,
        isUserJoined: PropTypes.bool,
        winner: PropTypes.string,
        winnerName: PropTypes.string,
        participantCount: PropTypes.number
    }).isRequired,
    variant: PropTypes.oneOf(['active', 'upcoming', 'concluded']).isRequired
};

CompetitionSection.propTypes = {
    title: PropTypes.string.isRequired,
    competitions: PropTypes.array,
    variant: PropTypes.oneOf(['active', 'upcoming', 'concluded']).isRequired,
    emptyMessage: PropTypes.string
};

CompetitionsOverview.propTypes = {
    activeCompetitions: PropTypes.array,
    upcomingCompetitions: PropTypes.array,
    concludedCompetitions: PropTypes.array
};

export default CompetitionsOverview;
